
import {
    ApplicationUserModel,
    FindingModel,
    FindingManagerModel,
    PlanItemStateEnum,
    TenantUserModel,
} from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudDataStats } from '@/libs/core/+state/models/crud-data-stats';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import Page from '@/Page.vue';
import { Component, Prop, Watch, Ref } from 'vue-property-decorator';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { findingsStore } from '../+state/store';
import { locationsStore } from '@/libs/locations/+state/store';

@Component({
    components: {
        FindingStatusChip: () => import('@/libs/common/components-smart/FindingStatusChip.vue'),
    },
})
export default class Findings extends Page {
    filterCategory: Array<string> | null = null;
    filterAudit: Array<string> | null = null;
    filterUser: Array<string> | null = null;
    filterDepartment: Array<string> | null = null;
    filterPosition: Array<string> | null = null;
    filterLocation: Array<string> | null = null;
    filterWorkplace: Array<string> | null = null;
    filterPlanItemState: Array<string> | null = null;
    filterMeasureCount: number | null = null;
    filterFindingStatus: number | null = null;
    filterManagerUser: Array<string> | null = null;
    filterOnlyMine = true;
    showSecondLine = false;
    user: ApplicationUserModel = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    filterCorrectiveMeasure = 0;
    filterIsImplementationClosed = null;
    mobileFiltersShow = false;
    pageIndex = 1;
    pageSize = 25;
    pagingChanged = false;
    filterDelay: number | null = null;

    get isExternal(): boolean {
        return this.tenantUser?.isExternal === true;
    }

    get workplacesFilterByLocations(): string[] {
        return this.computeExpandedLocationIds();
    }

    get headers() {
        return [
            { text: this.$i18n.t('planItems.date'), value: 'time', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.templateName'), value: 'response.planItem.audit.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.evidenceNumber'), value: 'evidenceNumber', align: 'left', sortable: false },
            {
                text: this.$i18n.t('planItems.workerAuditor'),
                value: 'response.planItem.tenantUser.user.displayName',
                align: 'left',
                sortable: false,
            },
            { text: this.$i18n.t('planItems.workplace.name'), value: 'workplace.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.station.name'), value: 'station.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.shiftType.name'), value: 'shiftType.name', align: 'left', sortable: false },
            {
                text: this.$i18n.t('planItems.findingManagers'),
                value: 'findingManagers',
                align: 'left',
                sortable: false,
                width: '14%',
            },
            { text: this.$i18n.t('planItems.findingDescription'), value: 'description', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.processUntil'), value: 'processUntil', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.processedAt'), value: 'processedAt', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.measureCount'), value: 'measureCount', align: 'left', sortable: false },
            { text: this.$i18n.t('findings.findingStatus'), value: 'findingStatus', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.processDelayDays'), value: 'processDelayDays', align: 'left', sortable: false },
            { text: '', value: 'id', align: 'left', sortable: false },
        ];
    }

    @Prop() tenantId!: string;

    @Watch('filterCategory')
    @Watch('filterAudit')
    @Watch('filterUser')
    @Watch('filterDepartment')
    @Watch('filterPosition')
    @Watch('filterLocation')
    @Watch('filterWorkplace')
    @Watch('filterMeasureCount')
    @Watch('filterFindingStatus')
    @Watch('filterOnlyMine')
    @Watch('filterManagerUser')
    @Watch('filterDelay')
    filtersChanged() {
        this.pageIndex = 1;
        this.load();
    }

    @Watch('pageIndex')
    @Watch('pageSize')
    paginationChanged() {
        this.saveQuery();
        if (this.pagingChanged) {
            this.pagingChanged = false;
            return;
        }
        this.load();
    }

    get loading(): boolean {
        return findingsStore.useGetter(CrudGetter.PendingBasic);
    }

    get items(): FindingModel[] {
        return [...findingsStore.useGetter(CrudGetter.DataBasic)];
    }

    get totalRows(): number {
        return ({ ...findingsStore.useGetter(CrudGetter.DataStatsBasic) } as CrudDataStats)?.rowCount;
    }

    get downloading(): boolean {
        return findingsStore.useGetter(CrudGetter.Downloading);
    }

    get delayFilterOptions(): { text: string; value: number }[] {
        return [
            { text: this.$t('measures.oneAndMoreDays').toString(), value: 1 },
            { text: this.$t('measures.twoAndMoreDays').toString(), value: 2 },
            { text: this.$t('measures.threeAndMoreDays').toString(), value: 3 },
            { text: this.$t('measures.fourAndMoreDays').toString(), value: 4 },
            { text: this.$t('measures.sevenAndMoreDays').toString(), value: 7 },
            { text: this.$t('measures.fourteenAndMoreDays').toString(), value: 14 },
            { text: this.$t('measures.thirtyAndMoreDays').toString(), value: 30 },
        ];
    }

    get measureCountFilterOptions(): { text: string; value: number }[] {
        return [
            { text: this.$t('findings.noMeasures').toString(), value: 0 },
            { text: this.$t('findings.oneAndMore').toString(), value: 1 },
            { text: this.$t('findings.twoAndMore').toString(), value: 2 },
            { text: this.$t('findings.threeAndMore').toString(), value: 3 },
            { text: this.$t('findings.fourAndMore').toString(), value: 4 },
            { text: this.$t('findings.fiveAndMore').toString(), value: 5 },
        ];
    }

    get findingStatusFilterOptions(): { text: string; value: number }[] {
        return [
            { text: this.$t('findings.measureCreated').toString(), value: 1 },
            { text: this.$t('findings.needsToCreateMeasure').toString(), value: 0 },
        ];
    }

    mounted() {
        if (this.getQueryInt('pageSize') != null) {
            this.pageSize = this.getQueryInt('pageSize') ?? 25;
        } else {
            if (window.localStorage.ptPageSize && Number(window.localStorage.ptPageSize)) {
                this.pageSize = Number(window.localStorage.ptPageSize);
            }
            this.saveQuery();
        }
        if (this.isExternal) {
            this.showSecondLine = false;
            this.filterOnlyMine = false;
            this.filterCategory = null;
            this.filterUser = null;
            this.filterDepartment = null;
            this.filterPosition = null;
            this.filterManagerUser = null;
        }
        this.load();
    }

    load() {
        findingsStore.dispatch(CrudAction.GetAllBasic, this.getPayload());
    }

    getFilters(): CrudQueryPredicate[] {
        let filters: CrudQueryPredicate[] = [
            { field: 'response.planItem.responseLock', op: 'eq', comparand: true },
            { field: 'response.planItem.disagreementCount', op: 'gt', comparand: 0 },
            {
                field: 'response.planItem.planItemState',
                op: 'in',
                comparand: [
                    PlanItemStateEnum.NeedsToCreateMeasures,
                    PlanItemStateEnum.NeedsToPerformMeasures,
                    PlanItemStateEnum.NeedsToVerifyMeasures,
                    PlanItemStateEnum.AllMeasuresVerified,
                ],
            },
        ];
        let auditFilters: CrudQueryPredicate[] = [];
        let categoryFilters: CrudQueryPredicate[] = [];
        let userFilters: CrudQueryPredicate[] = [];
        let departmentFilters: CrudQueryPredicate[] = [];
        let positionFilters: CrudQueryPredicate[] = [];
        let workplaceFilters: CrudQueryPredicate[] = [];
        let planItemStateFilters: CrudQueryPredicate[] = [];
        if (!this.tenantUser.accessAllCategories) {
            filters.push({ field: 'response.planItem.audit.category.categoryUsers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
        }
        if (this.filterManagerUser && this.filterManagerUser.length > 0) {
            filters.push({ field: 'findingManagers.tenantUserId', op: 'in', comparand: this.filterManagerUser });
        } else if (this.filterOnlyMine && !this.isExternal) {
            //filters.push({ field: 'findingManagers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
            filters.push({
                field: 'wrap',
                op: 'wrap',
                comparand: [
                    { field: 'findingManagers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! }
                ],
            });

        }
        if (this.filterCategory && this.filterCategory.length > 0) {
            this.filterCategory.forEach((e: string) => {
                if (e) {
                    categoryFilters.push({
                        field: 'response.planItem.audit.categoryId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterAudit && this.filterAudit.length > 0) {
            this.filterAudit.forEach((e: string) => {
                if (e) {
                    auditFilters.push({
                        field: 'response.planItem.auditId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterUser && this.filterUser.length > 0) {
            this.filterUser.forEach((e: string) => {
                if (e) {
                    userFilters.push({
                        field: 'response.planItem.planItemAuditTenantUsers.tenantUserId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterDepartment && this.filterDepartment.length > 0) {
            this.filterDepartment.forEach((e: string) => {
                if (e) {
                    departmentFilters.push({
                        field: 'response.planItem.tenantUser.departmentId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterPosition && this.filterPosition.length > 0) {
            this.filterPosition.forEach((e: string) => {
                if (e) {
                    positionFilters.push({
                        field: 'response.planItem.tenantUser.positionId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        const expandedLocationIds = this.computeExpandedLocationIds();
        if (expandedLocationIds.length > 0) {
            filters.push({ field: 'response.planItem.workplace.locationId', op: 'in', comparand: expandedLocationIds });
        }
        if (this.filterWorkplace && this.filterWorkplace.length > 0) {
            this.filterWorkplace.forEach((e: string) => {
                if (e) {
                    workplaceFilters.push({
                        field: 'response.planItem.workplaceId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        // finding status filter (derived from measureCount)
        if (this.filterFindingStatus != null && !isNaN(this.filterFindingStatus)) {
            if (this.filterFindingStatus === 0) {
                filters.push({ field: 'measureCount', op: 'eq', comparand: 0 });
            } else if (this.filterFindingStatus > 0) {
                filters.push({ field: 'measureCount', op: 'gt', comparand: 0 });
            }
        }
        // measureCount filter
        if (this.filterMeasureCount != null && !isNaN(this.filterMeasureCount)) {
            if (this.filterMeasureCount === 0) {
                filters.push({ field: 'measureCount', op: 'eq', comparand: 0 });
            } else if (this.filterMeasureCount > 0) {
                filters.push({ field: 'measureCount', op: 'gte', comparand: this.filterMeasureCount });
            }
        }
        if (this.filterDelay && !isNaN(this.filterDelay)) {
            filters.push({
                field: 'processDelayDays',
                op: 'gte',
                comparand: this.filterDelay,
            });
        }

        if (auditFilters.length > 0) {
            filters.push({
                field: 'response.planItem.auditId',
                op: 'wrap',
                comparand: auditFilters,
            });
        }
        if (categoryFilters.length > 0) {
            filters.push({
                field: 'response.planItem.audit.categoryId',
                op: 'wrap',
                comparand: categoryFilters,
            });
        }
        if (userFilters.length > 0) {
            filters.push({
                field: 'response.planItem.planItemAuditTenantUsers.tenantUserId',
                op: 'wrap',
                comparand: userFilters,
            });
        }
        if (departmentFilters.length > 0) {
            filters.push({
                field: 'response.planItem.tenantUser.departmentId',
                op: 'wrap',
                comparand: departmentFilters,
            });
        }
        if (positionFilters.length > 0) {
            filters.push({
                field: 'response.planItem.tenantUser.positionId',
                op: 'wrap',
                comparand: positionFilters,
            });
        }
        if (workplaceFilters.length > 0) {
            filters.push({
                field: 'response.planItem.workplaceId',
                op: 'wrap',
                comparand: workplaceFilters,
            });
        }
        // keep planItemStateFilters wrapper only if used elsewhere (currently not used)
        return filters;
    }

    computeExpandedLocationIds(): string[] {
        const selected = this.filterLocation ?? [];
        if (selected.length === 0) return [];
        const locations = (locationsStore.useGetter(CrudGetter.Data) ?? []) as any[];
        const selectedSet = new Set<string>(selected);
        const descendantIds = locations
            .filter((loc: any) =>
                Array.isArray(loc.locationParents) &&
                loc.locationParents.some((p: any) => p.parentalLocationId && selectedSet.has(p.parentalLocationId))
            )
            .map((loc: any) => loc.id)
            .filter(Boolean);
        return Array.from(new Set<string>([...selected, ...descendantIds]));
    }

    getPayload() {
        return createCrudQueryPayload<FindingModel>({ field: 'response.planItem.time', index: 1, order: 'desc' }, this.getFilters(), {
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
        });
    }

    exportClick() {
        const filters = this.getFilters();
        findingsStore.dispatch(
            CrudAction.Download,
            createCrudQueryPayload<FindingModel>({ field: 'response.planItem.time', index: 1, order: 'desc' }, filters)
        );
    }

    saveQuery() {
        this.setQuery('pageSize', this.pageSize?.toString());
        window.localStorage.ptPageSize = this.pageSize?.toString();
    }

    getImplementUntil(item: any) {
        return item.processUntil || null;
    }

    getImplementationTime(item: any) {
        return item.processedAt || null;
    }

    getImplementationDelayDays(item: any) {
        return item.processDelayDays != null ? item.processDelayDays : null;
    }

    getFindingManagersCount(item: any) {
        const total = Array.isArray(item?.findingManagers) ? item.findingManagers.length : 0;
        return total > 1 ? total - 1 : 0;
    }

    getFindingManagersCountString(item: any) {
        const extra = this.getFindingManagersCount(item);
        return extra > 0 ? `(+${extra})` : '';
    }

    getSortedFindingManagers(item: any): FindingManagerModel[] {
        const currentTenantUserId = this.tenantUser?.id ?? null;
        const managers: FindingManagerModel[] = Array.isArray(item?.findingManagers)
            ? [...(item.findingManagers as FindingManagerModel[])]
            : [];
        managers.sort((a, b) => {
            const aId = (a && (a.tenantUserId ?? a.tenantUser?.id)) || null;
            const bId = (b && (b.tenantUserId ?? b.tenantUser?.id)) || null;
            const aIsCurrent = !!currentTenantUserId && aId === currentTenantUserId;
            const bIsCurrent = !!currentTenantUserId && bId === currentTenantUserId;
            if (aIsCurrent && !bIsCurrent) return -1;
            if (bIsCurrent && !aIsCurrent) return 1;
            const aName = (a && a.tenantUser && (a.tenantUser as any).user && (a.tenantUser as any).user.displayName) || '';
            const bName = (b && b.tenantUser && (b.tenantUser as any).user && (b.tenantUser as any).user.displayName) || '';
            return aName.localeCompare(bName, 'cs', { sensitivity: 'base' });
        });
        return managers;
    }

    hasMeasures(item: any): boolean {
        const value = this.getMeasureCount(item);
        return typeof value === 'number' && value > 0;
    }

    getMeasureCount(item: any): number {
        const raw = (item && (item as any).measureCount) as number | string | null | undefined;
        const num = typeof raw === 'string' ? Number(raw) : raw;
        return typeof num === 'number' && !isNaN(num) ? num : 0;
    }

    getFindingStatusText(item: any): string {
        return this.hasMeasures(item)
            ? this.$t('findings.measureCreated').toString()
            : this.$t('findings.needsToCreateMeasure').toString();
    }

    getFindingStatusValue(item: any): number {
        return this.hasMeasures(item) ? 1 : 0;
    }
}
